/**
 * 複数決済併用（分割払い）コンポーネント
 * 現金+クレジットカード等の複数決済方法での支払いを管理
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CreditCard as CardIcon,
  LocalAtm as CashIcon,
  QrCode as QrIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// 決済方法アイコン
const PAYMENT_ICONS = {
  cash: CashIcon,
  credit_card: CardIcon,
  qr_code: QrIcon,
};

// 決済状態
const SPLIT_STATUS = {
  EDITING: 'editing',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ERROR: 'error',
};

const SplitPayment = ({
  totalAmount,
  orderId,
  storeId,
  onSuccess,
  onError,
  onCancel,
  availableMethods = [],
}) => {
  const [status, setStatus] = useState(SPLIT_STATUS.EDITING);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState(null);
  const [remainingAmount, setRemainingAmount] = useState(totalAmount);
  const [processedPayments, setProcessedPayments] = useState([]);
  const [showMethodDialog, setShowMethodDialog] = useState(false);
  const [currentPaymentIndex, setCurrentPaymentIndex] = useState(null);

  // 残金計算
  useEffect(() => {
    const paid = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
    setRemainingAmount(totalAmount - paid);
  }, [payments, totalAmount]);

  // 決済追加
  const addPayment = (method) => {
    const newPayment = {
      id: Date.now(),
      method,
      amount: remainingAmount > 0 ? remainingAmount : 0,
      status: 'pending',
      reference: '',
    };
    setPayments(prev => [...prev, newPayment]);
    setShowMethodDialog(false);
  };

  // 決済削除
  const removePayment = (index) => {
    setPayments(prev => prev.filter((_, i) => i !== index));
  };

  // 金額変更
  const updateAmount = (index, amount) => {
    const numAmount = parseInt(amount, 10) || 0;
    setPayments(prev => prev.map((p, i) =>
      i === index ? { ...p, amount: numAmount } : p
    ));
  };

  // 参照番号変更
  // const updateReference = (index, reference) => {
  //   setPayments(prev => prev.map((p, i) =>
  //     i === index ? { ...p, reference } : p
  //   ));
  // };

  // バリデーション
  const validatePayments = () => {
    if (payments.length === 0) {
      setError('少なくとも1つの決済方法を追加してください');
      return false;
    }

    const totalPaid = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
    if (totalPaid !== totalAmount) {
      setError(`合計金額が一致しません（差額: ¥${Math.abs(totalAmount - totalPaid).toLocaleString()}）`);
      return false;
    }

    if (payments.some(p => !p.amount || p.amount <= 0)) {
      setError('すべての決済に金額を入力してください');
      return false;
    }

    return true;
  };

  // 決済処理
  const processPayments = async () => {
    if (!validatePayments()) return;

    setStatus(SPLIT_STATUS.PROCESSING);
    setError(null);

    const results = [];
    let hasError = false;

    for (let i = 0; i < payments.length; i++) {
      const payment = payments[i];
      setCurrentPaymentIndex(i);

      try {
        // 決済処理（手動記録として処理）
        const response = await api.post('/payments/manual', {
          orderId,
          paymentMethodId: payment.method.id,
          amount: payment.amount,
          reference: payment.reference || undefined,
          note: `分割払い ${i + 1}/${payments.length}`,
        });

        if (response.data.success) {
          results.push({
            ...payment,
            status: 'success',
            transactionId: response.data.sale?.id,
          });
        } else {
          throw new Error(response.data.message || '決済処理に失敗しました');
        }
      } catch (err) {
        console.error(`決済処理エラー (${payment.method.name}):`, err);
        results.push({
          ...payment,
          status: 'error',
          error: err.message,
        });
        hasError = true;
        break;
      }
    }

    setProcessedPayments(results);
    setCurrentPaymentIndex(null);

    if (hasError) {
      setStatus(SPLIT_STATUS.ERROR);
      setError('一部の決済処理でエラーが発生しました');
      onError?.(new Error('分割払い処理中にエラーが発生しました'));
    } else {
      setStatus(SPLIT_STATUS.COMPLETED);
      onSuccess?.({
        payments: results,
        totalAmount,
      });
    }
  };

  // 決済方法アイコン取得
  const getPaymentIcon = (method) => {
    const Icon = PAYMENT_ICONS[method?.category] || CashIcon;
    return <Icon />;
  };

  // 編集画面
  if (status === SPLIT_STATUS.EDITING) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          複数決済併用
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          複数の決済方法を組み合わせてお支払いできます
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 決済リスト */}
        <List sx={{ bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
          {payments.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="決済方法が追加されていません"
                secondary="下のボタンから決済方法を追加してください"
              />
            </ListItem>
          ) : (
            payments.map((payment, index) => (
              <ListItem
                key={payment.id}
                sx={{ bgcolor: 'white', mb: 0.5, borderRadius: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  {getPaymentIcon(payment.method)}
                </Box>
                <ListItemText
                  primary={payment.method.name}
                  secondary={payment.method.category}
                />
                <TextField
                  type="number"
                  size="small"
                  value={payment.amount}
                  onChange={(e) => updateAmount(index, e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                  }}
                  sx={{ width: 150, mr: 1 }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removePayment(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))
          )}
        </List>

        {/* 決済追加ボタン */}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setShowMethodDialog(true)}
          fullWidth
          sx={{ mb: 2 }}
        >
          決済方法を追加
        </Button>

        <Divider sx={{ my: 2 }} />

        {/* 金額サマリー */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              合計金額
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              ¥{totalAmount.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              入力済み
            </Typography>
            <Typography variant="body1">
              ¥{(totalAmount - remainingAmount).toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              残金
            </Typography>
            <Typography
              variant="body1"
              fontWeight="bold"
              color={remainingAmount === 0 ? 'success.main' : remainingAmount < 0 ? 'error.main' : 'warning.main'}
            >
              ¥{remainingAmount.toLocaleString()}
            </Typography>
          </Box>
        </Box>

        {remainingAmount !== 0 && (
          <Alert severity={remainingAmount < 0 ? 'error' : 'warning'} sx={{ mb: 2 }}>
            {remainingAmount < 0
              ? '入力金額が合計を超えています'
              : '残金があります。すべての金額を入力してください'}
          </Alert>
        )}

        {/* アクションボタン */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ flex: 1 }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={processPayments}
            disabled={payments.length === 0 || remainingAmount !== 0}
            sx={{ flex: 2 }}
          >
            決済を実行
          </Button>
        </Box>

        {/* 決済方法選択ダイアログ */}
        <Dialog
          open={showMethodDialog}
          onClose={() => setShowMethodDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>決済方法を選択</DialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              {availableMethods.map((method) => (
                <Grid item xs={6} key={method.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => addPayment(method)}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', py: 1.5 }}>
                      {getPaymentIcon(method)}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {method.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowMethodDialog(false)}>
              キャンセル
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // 処理中画面
  if (status === SPLIT_STATUS.PROCESSING) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          決済処理中...
        </Typography>
        {currentPaymentIndex !== null && (
          <Typography variant="body2" color="text.secondary">
            {payments[currentPaymentIndex]?.method.name} を処理中
            ({currentPaymentIndex + 1}/{payments.length})
          </Typography>
        )}
      </Box>
    );
  }

  // 完了画面
  if (status === SPLIT_STATUS.COMPLETED) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          分割払い完了
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          すべての決済が正常に処理されました
        </Typography>

        <List sx={{ bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
          {processedPayments.map((payment, index) => (
            <ListItem key={index}>
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                {getPaymentIcon(payment.method)}
              </Box>
              <ListItemText
                primary={payment.method.name}
                secondary={`¥${payment.amount.toLocaleString()}`}
              />
              <Chip
                label="完了"
                color="success"
                size="small"
              />
            </ListItem>
          ))}
        </List>

        <Button
          variant="contained"
          onClick={onCancel}
          fullWidth
        >
          閉じる
        </Button>
      </Box>
    );
  }

  // エラー画面
  if (status === SPLIT_STATUS.ERROR) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <WarningIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom color="error">
          エラーが発生しました
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <List sx={{ bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
          {processedPayments.map((payment, index) => (
            <ListItem key={index}>
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                {getPaymentIcon(payment.method)}
              </Box>
              <ListItemText
                primary={payment.method.name}
                secondary={
                  payment.status === 'error'
                    ? payment.error
                    : `¥${payment.amount.toLocaleString()}`
                }
              />
              <Chip
                label={payment.status === 'success' ? '完了' : 'エラー'}
                color={payment.status === 'success' ? 'success' : 'error'}
                size="small"
              />
            </ListItem>
          ))}
        </List>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ flex: 1 }}
          >
            閉じる
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setStatus(SPLIT_STATUS.EDITING);
              setError(null);
              setProcessedPayments([]);
            }}
            sx={{ flex: 1 }}
          >
            再試行
          </Button>
        </Box>
      </Box>
    );
  }

  return null;
};

export default SplitPayment;
